import { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { SAFETY_NONE_VALUE, SAFETY_OPTIONS } from "../types";
import { ScreenShell } from "./ScreenShell";

interface SafetyStepProps {
  readonly selected: readonly string[];
  readonly onContinue: (flags: string[]) => void;
}

/** Multi-select health-check step; "None of these" is exclusive with every flag. */
export function SafetyStep({ selected, onContinue }: SafetyStepProps) {
  const [flags, setFlags] = useState<readonly string[]>(selected);
  const noneSelected = flags.length === 0;

  const toggleFlag = (value: string) => {
    setFlags((prev) =>
      prev.includes(value) ? prev.filter((flag) => flag !== value) : [...prev, value],
    );
  };

  const selectNone = () => {
    setFlags([]);
  };

  return (
    <ScreenShell
      title="A quick health check"
      subtext="We ask so we can keep your plan safe. In these cases we'll skip automated calorie deficits and suggest professional guidance."
      footer={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => onContinue([...flags])}
        >
          Continue
        </Button>
      }
    >
      <div role="group" aria-label="Health check" className="flex flex-col gap-3">
        {SAFETY_OPTIONS.map((option) => {
          const isSelected = flags.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              onClick={() => toggleFlag(option.value)}
              className={cn(
                "flex min-h-[56px] w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left",
                "select-none transition-all duration-200 ease-out active:scale-[0.98]",
                isSelected ? "border-accent bg-accent-light" : "border-border bg-surface",
              )}
            >
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2",
                  isSelected ? "border-accent bg-accent text-white" : "border-border bg-surface",
                )}
                aria-hidden="true"
              >
                {isSelected && <Check size={14} strokeWidth={3} />}
              </span>
              <span className="flex-1 text-base font-semibold text-text">{option.title}</span>
            </button>
          );
        })}
        <button
          type="button"
          role="checkbox"
          aria-checked={noneSelected}
          onClick={selectNone}
          className={cn(
            "flex min-h-[56px] w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left",
            "select-none transition-all duration-200 ease-out active:scale-[0.98]",
            noneSelected ? "border-accent bg-accent-light" : "border-border bg-surface",
          )}
        >
          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2",
              noneSelected ? "border-accent bg-accent text-white" : "border-border bg-surface",
            )}
            aria-hidden="true"
          >
            {noneSelected && <Check size={14} strokeWidth={3} />}
          </span>
          <span className="flex-1 text-base font-semibold text-text">None of these</span>
        </button>
      </div>
      <p className="sr-only">{SAFETY_NONE_VALUE} clears every other selection.</p>
    </ScreenShell>
  );
}
